import { TemplateResult } from 'lit';
import { html } from 'lit/static-html.js';
import {
  customElement,
  property,
  queryAssignedElements,
  state,
} from 'lit/decorators.js';
import { OutlineElement } from '../../base/outline-element/outline-element';
import {
  AllowedThemeValues,
  SicAccordion,
} from '../sic-accordion/sic-accordion';

/**
 * The Sic Accordion Container component
 * @element sic-accordion-container
 * @extends OutlineElement
 *
 * @slot default slot: for only <sic-accordion> elements.
 *
 * @prop {boolean} first-active - whether the first panel starts active/open
 * @prop {boolean} single-active - whether the panel is active/open
 * @prop {string} theme - controls style of accordions
 */
@customElement('sic-accordion-container')
export class SicAccordionContainer extends OutlineElement {
  /**
   * Set to 'true' to create an accordion that has first accordion active.
   */
  @property({ type: Boolean, attribute: 'first-active' })
  firstActive = false;

  /**
   * Set to 'true' to create an accordion that can only have one active at a time.
   */
  @property({ type: Boolean, attribute: 'single-active' })
  singleActive = false;

  /**
   * Theme controls style of accordion button and panel content.
   */
  @property({ type: String, attribute: 'theme', reflect: true })
  theme: AllowedThemeValues = 'normal';

  /**
   * Reference to <sic-accordion> in default slot.
   */
  @queryAssignedElements({ slot: '' })
  panels: SicAccordion[];

  @state() panelsCleaned: SicAccordion[];

  /**
   * Sets 'theme' and 'active' states on
   * child <sic-accordion> elements when the accordion updates.
   */
  firstUpdated() {
    // Check if panels is an array of SicAccordions, if not update to be.
    if (
      this.panels &&
      this.panels.every(panel => panel instanceof SicAccordion)
    ) {
      this.panelsCleaned = this.panels;
    } else {
      this.panelsCleaned = this.panels
        .map(panel => panel.querySelector('sic-accordion'))
        .filter(panel => panel !== null) as SicAccordion[];
    }
    if (this.firstActive) {
      this.panelsCleaned?.[0].setAttribute('active', 'active');
    }
    if (this.theme) {
      this.panelsCleaned.map(panel => panel.setAttribute('theme', this.theme));
    } else {
      this.panelsCleaned.map(panel => panel.removeAttribute('theme'));
    }
  }
  updated() {}

  render(): TemplateResult {
    return html`
      <div class="accordion" @keydown=${this.handleKeyboardNav}>
        <slot></slot>
      </div>
    `;
  }

  /**
   * @param event
   * @returns void
   * Adds up/down arrow keyboard navigation.
   * TODO: try to refactor to something simpler.
   */
  handleKeyboardNav(event: KeyboardEvent) {
    let index = 0;
    const panels = this.panels;
    const start = Array.prototype.indexOf.call(
      panels,
      event.target as HTMLElement
    );

    // bail on wrong key
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
      return;
    }
    if (event.key === 'ArrowDown') {
      if (panels?.length && start + 1 > panels?.length - 1) {
        index = 0;
      } else index = start + 1;
    }
    if (event.key === 'ArrowUp') {
      if (panels?.length && start - 1 < 0) {
        index = panels?.length - 1;
      } else index = start - 1;
    }
    const focusTarget = panels?.[index] as HTMLElement;
    focusTarget.shadowRoot?.querySelector('button')?.focus();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sic-accordion-container': SicAccordionContainer;
  }
}
